<template>
  <div class="admin-page">
    <div class="header">
      <div>
        <h1>平台管理端</h1>
        <p class="sub">商家审核、投诉处理、商品治理一体化看板</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" plain :loading="loading" @click="refreshAll">刷新数据</el-button>
        <el-dropdown @command="handleUserCommand">
          <span class="user-dropdown-link">
            {{ adminName }}
            <el-icon class="el-icon--right"><ArrowDown /></el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="profile">个人信息</el-dropdown-item>
              <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <el-row :gutter="16" class="stat-row">
      <el-col :xs="24" :md="8"><div class="card">入驻申请：{{ pendingApplicationCount }}</div></el-col>
      <el-col :xs="24" :md="8"><div class="card">待处理投诉：{{ pendingComplaintCount }}</div></el-col>
      <el-col :xs="24" :md="8"><div class="card">活跃商家：{{ activeMerchantCount }}</div></el-col>
    </el-row>

    <el-row :gutter="16" class="main-row">
      <el-col :xs="24" :lg="8">
        <div class="panel glass">
          <div class="panel-header">
            <h3>入驻申请审核</h3>
            <el-tag type="warning" size="small">可操作</el-tag>
          </div>

          <el-table :data="merchantApplications" stripe v-loading="loading" height="380" empty-text="暂无申请数据">
            <el-table-column prop="name" label="商家" min-width="120" />
            <el-table-column prop="category" label="类目" width="90" />
            <el-table-column label="状态" width="100">
              <template #default="scope">
                <el-tag :type="applicationTag(scope.row.status)" size="small">{{ applicationText(scope.row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="140" fixed="right">
              <template #default="scope">
                <el-button link type="success" :disabled="scope.row.status !== 'pending'" @click="reviewApplication(scope.row, 'approved')">通过</el-button>
                <el-button link type="danger" :disabled="scope.row.status !== 'pending'" @click="reviewApplication(scope.row, 'rejected')">驳回</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-col>

      <el-col :xs="24" :lg="8">
        <div class="panel glass">
          <div class="panel-header">
            <h3>投诉处理中心</h3>
            <el-tag type="danger" size="small">工单流</el-tag>
          </div>

          <el-table :data="complaintList" stripe v-loading="loading" height="380" empty-text="暂无投诉数据">
            <el-table-column prop="topic" label="问题" min-width="130" show-overflow-tooltip />
            <el-table-column prop="from" label="来源" width="90" />
            <el-table-column label="状态" width="100">
              <template #default="scope">
                <el-tag :type="complaintTag(scope.row.status)" size="small">{{ complaintText(scope.row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100" fixed="right">
              <template #default="scope">
                <el-button link type="primary" :disabled="scope.row.status === 'done'" @click="resolveComplaint(scope.row)">标记已处理</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-col>

      <el-col :xs="24" :lg="8">
        <div class="panel glass">
          <div class="panel-header">
            <h3>商品治理</h3>
            <el-tag type="success" size="small">真实接口</el-tag>
          </div>

          <el-table :data="productList" stripe v-loading="loading" height="380" empty-text="暂无商品数据">
            <el-table-column prop="title" label="商品" min-width="130" show-overflow-tooltip />
            <el-table-column label="状态" width="90">
              <template #default="scope">
                <el-switch
                  v-model="scope.row.enabled"
                  inline-prompt
                  active-text="上架"
                  inactive-text="下架"
                  @change="changeProductStatus(scope.row)"
                />
              </template>
            </el-table-column>
            <el-table-column label="操作" width="80" fixed="right">
              <template #default="scope">
                <el-button link type="danger" @click="removeProduct(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-col>
    </el-row>

    <div class="actions">
      <el-button type="primary" @click="goUserHome">去用户端首页</el-button>
      <el-button @click="goMerchant">切换商家端</el-button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowDown } from '@element-plus/icons-vue'
import { productService } from '../../api/product'

const router = useRouter()
const adminName = ref('平台管理员')
const loading = ref(false)

const merchantApplications = ref([
  { id: 1, name: '极客数码店', category: '数码', status: 'pending' },
  { id: 2, name: '新知图书馆', category: '图书', status: 'pending' },
  { id: 3, name: '舒适办公坊', category: '办公', status: 'pending' },
])

const complaintList = ref([
  { id: 11, topic: '物流延迟超过 72 小时', from: '用户', status: 'pending' },
  { id: 12, topic: '商品描述与实物不符', from: '用户', status: 'pending' },
  { id: 13, topic: '退款时效争议', from: '商家', status: 'done' },
])

const productList = ref([])

const pendingApplicationCount = computed(() => merchantApplications.value.filter(item => item.status === 'pending').length)
const pendingComplaintCount = computed(() => complaintList.value.filter(item => item.status === 'pending').length)
const activeMerchantCount = computed(() => merchantApplications.value.filter(item => item.status === 'approved').length + 8)

const parseProductData = (payload) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.records)) return payload.records
  if (Array.isArray(payload?.list)) return payload.list
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.records)) return payload.data.records
  if (Array.isArray(payload?.data?.list)) return payload.data.list
  return []
}

const loadProducts = async () => {
  const response = await productService.getProductList({ page: 1, size: 120 })
  const list = parseProductData(response.data)
  productList.value = list.map(item => ({
    ...item,
    enabled: Number(item.status ?? 1) === 1,
  }))
}

const refreshAll = async () => {
  loading.value = true
  try {
    await loadProducts()
  } catch (error) {
    ElMessage.error(error.response?.data?.message || error.message || '管理端数据加载失败')
  } finally {
    loading.value = false
  }
}

const applicationText = (status) => {
  if (status === 'approved') return '已通过'
  if (status === 'rejected') return '已驳回'
  return '待审核'
}

const applicationTag = (status) => {
  if (status === 'approved') return 'success'
  if (status === 'rejected') return 'danger'
  return 'warning'
}

const complaintText = (status) => (status === 'done' ? '已处理' : '待处理')
const complaintTag = (status) => (status === 'done' ? 'success' : 'danger')

const reviewApplication = (row, status) => {
  row.status = status
  ElMessage.success(`已${status === 'approved' ? '通过' : '驳回'}「${row.name}」申请`)
}

const resolveComplaint = (row) => {
  row.status = 'done'
  ElMessage.success('投诉已标记为处理完成')
}

const changeProductStatus = async (row) => {
  try {
    await productService.updateProductStatus(row.id, row.enabled ? 1 : 0)
    ElMessage.success(`商品「${row.title}」已${row.enabled ? '上架' : '下架'}`)
  } catch (error) {
    row.enabled = !row.enabled
    ElMessage.error(error.response?.data?.message || error.message || '状态更新失败')
  }
}

const removeProduct = async (row) => {
  try {
    await ElMessageBox.confirm(`确认删除商品「${row.title}」吗？`, '删除确认', {
      type: 'warning',
      confirmButtonText: '删除',
      cancelButtonText: '取消',
    })
    await productService.deleteProduct(row.id)
    productList.value = productList.value.filter(item => item.id !== row.id)
    ElMessage.success('商品删除成功')
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.message || error.message || '删除失败')
    }
  }
}

const logout = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('portalRole')
  router.push('/login')
}

const goProfile = () => {
  router.push('/profile')
}

const handleUserCommand = (command) => {
  if (command === 'profile') return goProfile()
  if (command === 'logout') return logout()
}

const goUserHome = () => {
  localStorage.setItem('portalRole', 'USER')
  router.push('/home')
}

const goMerchant = () => {
  localStorage.setItem('portalRole', 'MERCHANT')
  router.push('/merchant')
}

onMounted(async () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    adminName.value = user.nickname || user.username || '平台管理员'
  } catch {
    adminName.value = '平台管理员'
  }
  await refreshAll()
})
</script>

<style scoped>
.admin-page {
  min-height: 100vh;
  padding: 24px;
  color: #fff;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.header h1 {
  margin: 0;
}

.sub {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.92rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-dropdown-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: #fff;
  cursor: pointer;
}

.stat-row {
  margin-bottom: 16px;
}

.card {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 18px;
  text-align: center;
  font-size: 1.05rem;
  font-weight: 700;
}

.main-row {
  margin-bottom: 16px;
}

.panel {
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 12px 28px rgba(16, 12, 42, 0.18);
}

.glass {
  background: linear-gradient(145deg, rgba(86, 71, 146, 0.26), rgba(48, 32, 98, 0.28));
  border: 1px solid rgba(186, 171, 255, 0.25);
  backdrop-filter: blur(8px);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.panel-header h3 {
  margin: 0;
  font-size: 1.05rem;
}

.actions {
  margin-top: 4px;
  display: flex;
  gap: 12px;
}

:deep(.el-table) {
  --el-table-tr-bg-color: rgba(255, 255, 255, 0.06);
  --el-table-header-bg-color: rgba(255, 255, 255, 0.14);
  --el-table-row-hover-bg-color: rgba(125, 211, 252, 0.14);
  --el-table-border-color: rgba(255, 255, 255, 0.1);
  background: transparent;
  border-radius: 10px;
}

:deep(.el-table th.el-table__cell) {
  color: rgba(245, 243, 255, 0.9);
}

:deep(.el-table td.el-table__cell) {
  color: rgba(241, 245, 255, 0.92);
  background: rgba(255, 255, 255, 0.04) !important;
}

:deep(.el-table--striped .el-table__body tr.el-table__row--striped td.el-table__cell) {
  background: rgba(255, 255, 255, 0.12) !important;
}

:deep(.el-table__inner-wrapper::before) {
  display: none;
}

:deep(.el-table tr),
:deep(.el-table__body-wrapper),
:deep(.el-table__header-wrapper) {
  background: transparent;
}
</style>
